import { SectionCard } from "@/deps/ui-library";
import { useDocumentEmails } from "@/api/queries";
import type { DocumentEmailRecipient, EmailsOf } from "@/api/types";
import { formatDate } from "@/lib/dates";

/** What a person needs to know about one recipient, and what to do if it didn't arrive. */
function outcome({ state, reason }: DocumentEmailRecipient): { text: string; bad?: boolean } {
  switch (state) {
    case "preparing":
      return { text: "Preparing the PDF" };
    case "sending":
      return { text: "Sending" };
    case "sent":
      return { text: "Sent" };
    case "bounced":
      return { text: "Bounced. Check the address.", bad: true };
    case "skipped":
      if (reason === "preference_off") return { text: "Not sent. They unsubscribed.", bad: true };
      if (reason === "suppressed") {
        return { text: "Not sent. This address bounced before.", bad: true };
      }
      return { text: "Not sent.", bad: true };
    default:
      return { text: "Not sent. Try again.", bad: true };
  }
}

/** The emails sent about a document, and how each one went. Nothing here until one has been sent. */
export function DocumentEmails({ of }: { of: EmailsOf }) {
  const { data: emails } = useDocumentEmails(of);
  if (!emails || emails.length === 0) return null;

  return (
    <SectionCard title="Emails">
      <ul className="flex flex-col gap-3">
        {emails.map((email) => (
          <li key={email.id} className="flex flex-col gap-1 text-xs">
            <span className="text-fg font-medium">{email.subject}</span>
            <span className="text-fg-muted">{formatDate(email.sentAt.slice(0, 10))}</span>
            {email.recipients.map((recipient) => {
              const { text, bad } = outcome(recipient);
              return (
                <span key={recipient.address} className="flex flex-wrap justify-between gap-x-2">
                  <span className="text-fg-muted break-all">{recipient.address}</span>
                  <span className={bad ? "text-bad-text" : "text-fg"}>{text}</span>
                </span>
              );
            })}
          </li>
        ))}
      </ul>
    </SectionCard>
  );
}
